import React from 'react';
import { Helmet } from 'react-helmet-async';
import { URLS } from '@shared/utils/urls';
import { useBundledApplicatorEnabledIfProvided } from '@shared/utils/styleApplicator/ThemeStylesProvider';

/**
 * Loads the style applicator unless retry SSR already executed an inline copy.
 */
export const StyleApplicatorScript: React.FC = () => {
  // PRNT-4898: when the bundled applicator is on, theme art renders as React (ThemeSprites)
  // and this external script must not load.
  //
  // Only an ENABLED flag skips the tag — an unresolved flag reads false and keeps it. Skipping
  // while the flag is still resolving looks tempting (it would avoid a redundant fetch for
  // treatment users whose variant SSR didn't seed) but it costs far more than it saves: in the
  // SSR HTML this tag is discovered by the preload scanner during parse, so withholding it
  // delays the art for every control user until Amplitude answers. SSR seeds the variant for
  // flagged-on traffic, so treatment already omits the tag at the source.
  //
  // The page's latched decision, so a late resolution cannot pull the script out from under a
  // page whose writers already committed to the legacy path. Read only where a theme-art surface
  // mounted BundledApplicatorProvider: this component also sits in AdminHead on every admin page,
  // and consulting the remote flag there would record an Amplitude exposure per admin page view.
  const isBundledApplicatorEnabled = useBundledApplicatorEnabledIfProvided();
  if (isBundledApplicatorEnabled) {
    return null;
  }

  if (typeof window !== 'undefined' && window.__SSR_STYLE_APPLICATOR_INLINED__) {
    return null;
  }

  return (
    <Helmet>
      <script src={URLS.styleApplicatorLib} />
    </Helmet>
  );
};
